import type { RefObject } from 'react';

/** The space between the composer box and a popover above or below it (the drawn 8 px). */
export const GAP = 8;
/**
 * The least room kept between a popover and the viewport's edges: the side panel's own inset
 * around its composer (AiCioPanel's `p-3`), so a popover aligned to that box's inline end ends
 * flush with it, as drawn.
 */
export const GUTTER = 12;

export type PanelSide = 'top' | 'bottom';

/**
 * The side of the composer box a popover opens on, for content `height` px tall with `above` and
 * `below` px of room: below when it fits there (the full page's new chat, whose composer sits
 * mid-page), else above when it fits there (the docked composer), else the side with more room,
 * where its content scrolls.
 */
export function panelSide(height: number, above: number, below: number): PanelSide {
  if (height <= below) return 'bottom';
  if (height <= above) return 'top';
  return above > below ? 'top' : 'bottom';
}

/** The rect a popover is placed against, read each time it is placed (a virtual anchor). */
export interface PanelAnchor {
  getBoundingClientRect: () => DOMRect;
  /** The chip: floating-ui follows the scrolls of its ancestors. */
  readonly contextElement: Element | undefined;
}

const NO_RECT = { x: 0, y: 0, width: 0, height: 0, top: 0, bottom: 0, left: 0, right: 0 };

/**
 * A popover's anchor: the composer box, so the popover opens clear of it and lines up with its
 * inline end (`align="end"`); the chip's rect when there is no box. Read every frame while the
 * popover is open, so it follows both.
 */
export function boxAnchor(
  boxRef: RefObject<HTMLElement | null>,
  chipRef: RefObject<HTMLElement | null>,
): PanelAnchor {
  return {
    getBoundingClientRect: () =>
      (boxRef.current ?? chipRef.current)?.getBoundingClientRect() ?? {
        ...NO_RECT,
        toJSON: () => NO_RECT,
      },
    get contextElement() {
      return chipRef.current ?? undefined;
    },
  };
}

/** The viewport's height without a scrollbar; jsdom lays out nothing, so its window's. */
export function viewportHeight(): number {
  return document.documentElement.clientHeight || window.innerHeight;
}
